import loadImage from 'blueimp-load-image'

export const SUPPORTED_IMAGE_TYPES = {
  BMP: 'image/bmp',
  GIF: 'image/gif',
  // it is important that this is 'jpeg' and not 'jpg' as converting from
  // canvas to blob won't work properly if it is 'jpg'
  JPG: 'image/jpeg',
  PNG: 'image/png',
}

const getBlobFromBase64 = (b64Data, contentType = '', sliceSize = 512) => {
  const type = contentType
  const size = sliceSize
  const byteCharacters = atob(b64Data)
  const byteArrays: Uint8Array[] = []
  let offset = 0
  while (offset < byteCharacters.length) {
    const slice = byteCharacters.slice(offset, offset + size)
    const byteNumbers = new Array(slice.length)
    let i = 0
    while (i < slice.length) {
      byteNumbers[i] = slice.charCodeAt(i)
      i += 1
    }
    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
    offset += size
  }
  return new Blob(byteArrays, { type })
}

// default width / height are constraints of the large version in message attachment uploader
const processImage = ({ file, fileType }) => {
  return new Promise((resolve, reject) => {
    const fr = loadImage(
      file,
      (canvas) => {
        const { height: imageHeight, width: imageWidth } = canvas
        const src = canvas.toDataURL(fileType || SUPPORTED_IMAGE_TYPES.JPG, 0.8)
        const blob = getBlobFromBase64(
          src.split(',')[1],
          fileType || SUPPORTED_IMAGE_TYPES.PNG,
        )

        return resolve({
          blob,
          imageHeight,
          imageWidth,
          objectURL: URL.createObjectURL(blob),
        })
      },
      {
        canvas: true,
        maxHeight: 4000,
        maxWidth: 2000,
        orientation: true,
      },
    )
    fr.onerror = (err) => {
      return reject({ ...err })
    }
  })
}

export const isValidFileType = (
  file: File,
): Promise<{
  blob: Blob
  objectURL: string
  imageHeight: number
  imageWidth: number
}> => {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    let fileType
    fr.onloadend = () => {
      const arr = new Uint8Array(fr.result as ArrayBuffer).subarray(0, 4)
      let header = ''
      let i = 0
      while (i < arr.length) {
        header += arr[i].toString(16)
        i++
      }
      if (/ffd8ff/i.test(header)) {
        // image/jpg
        fileType = SUPPORTED_IMAGE_TYPES.JPG
      } else if (/424D/i.test(header)) {
        // image/bmp
        fileType = SUPPORTED_IMAGE_TYPES.BMP
      } else {
        switch (header) {
          case '47494638': // image/gif
            fileType = SUPPORTED_IMAGE_TYPES.GIF
            break
          case '89504e47': // image/png
            fileType = SUPPORTED_IMAGE_TYPES.PNG
            break
          default:
            return reject(new Error('Unsupported image type'))
        }
      }
      if (fileType !== SUPPORTED_IMAGE_TYPES.GIF) {
        processImage({ file, fileType })
          .then(({ blob, imageHeight, imageWidth, objectURL }) => {
            return resolve({ blob, imageHeight, imageWidth, objectURL })
          })
          .catch((err) => reject({ ...err }))
      } else {
        const img = new Image()
        const objectURL = URL.createObjectURL(file)
        img.onload = () => {
          return resolve({
            blob: file,
            imageHeight: img.width,
            imageWidth: img.height,
            objectURL,
          })
        }
        img.src = objectURL
      }
    }
    fr.onerror = () => reject({ ...fr.error })
    fr.readAsArrayBuffer(file)
  })
}

export const precisionRound = (float: number, precision: number): number => {
  const factor = Math.pow(10, precision)
  return Math.round(float * factor) / factor
}
